import React, { useEffect } from 'react';
import { Radio, RadioGroup, FormControlLabel } from '@mui/material'
import { Label } from 'components/Form'
import { WrapField, WrapFieldContent } from './Styled';

/**
 * A form-bound Radio Button Group wrapping Material-UI components.
 * Supports standard single selection and a custom multi-selection mode.
 * * **Key Mechanics:**
 * - **Multi-Select (`multiple`):** When true, transforms the `input.value` into a dictionary object 
 * (e.g., `{ val1: 'val1', val2: 'val2' }`) and acts as a toggle. Note: This simulates Checkbox behavior using Radios.
 * - **Auto-Select First Option:** If instantiated without a `value`, it automatically fires `onChange` 
 * on mount with the `value` of the first item in the `options` array.
 * - **Value Fallback:** When mapping options, it prioritizes `opt.value` and falls back to `opt.id`.
 * - **Dynamic Labels:** Use `getLabel(opt)` to compute complex labels dynamically.
 * * @param {Object} props
 * @param {Object} props.input - Redux-form/final-form binding object: `{ value, onChange }`.
 * @param {Array<Object>} props.options - Array of radio choices. Expects shape: `{ id: string|number, label: string, value?: any }`.
 * @param {string} [props.label] - The main group label displayed above the radio list.
 * @param {boolean} [props.mandatory] - Marks the group label as required (typically adds a red asterisk via `<Label>`).
 * @param {boolean} [props.multiple] - Enables multi-select toggle mode. Changes `value` signature from string/number to an Object dictionary.
 * @param {boolean} [props.disabled] - Disables all radio buttons in the group.
 * @param {Function} [props.getLabel] - `(option) => ReactNode` - Custom renderer for individual radio option labels.
 * @param {string} [props.color] - Material-UI color prop passed to the `<Radio>` control (e.g., "primary", "secondary").
 * @param {boolean} [props.row] - If true, displays the radio buttons in a horizontal row instead of a column.
 * @param {string} [props.name] - Standard HTML name attribute for the group.
 * @param {string} [props.section] - Layout prop passed down to the `WrapField` container.
 * @param {string} [props.className] - CSS class applied to the MUI `RadioGroup`.
 * @param {string} [props.placeholder] - Destructured but currently unused in the render logic.
 * * @example
 * // Single selection
 * <RadioList 
 * input={{ value: selected, onChange: setSelected }} 
 * options={[{ id: 1, label: 'Yes', value: 'YES' }, { id: 2, label: 'No', value: 'NO' }]} 
 * />
 * * @example
 * // Multi selection (value becomes { YES: 'YES', MAYBE: 'MAYBE' })
 * <RadioList 
 * multiple
 * input={{ value: selectedObj, onChange: setSelectedObj }} 
 * options={optionsArray} 
 * />
 */
const RadioList = (props) => {
    const { disabled, getLabel, color, row, label, name, input, options, section, className, placeholder, multiple, mandatory } = props,
        { onChange, value } = input;

    const handlePlayerToggle = ({ target }) => {
        if (multiple) {
            let nChecked = { ...value }
            const nValue = target.value
            if (nChecked[nValue])
                delete nChecked[nValue]
            else nChecked[target.value] = target.value
            onChange(nChecked)
        }
        else {
            onChange(target.value)
        }
    }

    useEffect(() => {
        if (!value && options)
            onChange(options[0]?.value || options[0]?.id)
    }, [])

    return (
        <WrapField section={section}>
            <WrapFieldContent>
                {label && <Label label={label} mandatory={mandatory} />}
                <RadioGroup
                    row={row}
                    name={name}
                    // לא מתאים לרכיב הזה להיות מרובה בחירות, מתאים יותר לצאקבוקס
                    multiple={multiple}
                    className={className}
                    aria-labelledby="demo-radio-buttons-group-label"
                    onChange={handlePlayerToggle}
                    value={value}
                >
                    {options?.map(opt => {
                        const { id, label } = opt
                        return (
                            <FormControlLabel
                                key={id}
                                disabled={disabled}
                                value={opt.value || opt.id}
                                control={<Radio color={color} />}
                                label={getLabel ? getLabel(opt) : label}
                            // checked={multiple ? value[id] : value}
                            />
                        )
                    })}
                </RadioGroup>
            </WrapFieldContent>
        </WrapField>
    );
}

export default RadioList
